import { useCallback, useMemo, useState } from 'react'
import { string, object, SchemaOf } from 'yup'
import { Map } from 'immutable'
import { useSelector } from 'react-redux'
import { selectBrooklynServerUrl } from '../../config/redux/selectors'
import { selectAuth } from '../../auth/redux/selectors'
import { Result } from '../Result'
import { deleteMediaAsset } from '../../media-assets'

type Params = {
  id: string
}

const paramsValidationSchema: SchemaOf<Params> = object({
  id: string().required(),
})

type Status = 'removing' | 'success' | 'error' | 'idle'

export function useDeleteMediaAsset ({ onSuccess }: { onSuccess?: () => void } = {}) {
  const [status, setStatus] = useState<Status>('idle')

  const authDetails = useSelector<unknown, Map<string, unknown>>(selectAuth)
  const { isLoading: isConfigLoading, url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [error, setError] = useState<Error | string | null>(null)

  const remove = useCallback(
    async (params: Params) => {
      let createAssetResult: Map<string, unknown> | null = null

      const paramsOrError = Result.encase(() => paramsValidationSchema.validateSync(params))()

      if (paramsOrError.status === 'fail') {
        setError(paramsOrError.error)
        setStatus('error')
        return paramsOrError
      }

      try {
        setStatus('removing')
        createAssetResult = await deleteMediaAsset({
          auth: authDetails,
          server: brooklynUrl,
          data: Map(paramsOrError.result),
        }).then((response) => {
          if (typeof onSuccess === 'function') {
            onSuccess()
          }
          return response
        })
      } catch (e) {
        const errorMsg = typeof e === 'string' || e instanceof Error ? e : 'unknonw type of error'
        setError(errorMsg)
        setStatus('error')

        return Result.fail(errorMsg)
      }
      if (createAssetResult === null) {
        const errorMsg = 'Failed to delete media asset'
        setError(errorMsg)

        setStatus('error')
        return Result.fail(errorMsg)
      }

      setStatus('success')
      return Result.ok(createAssetResult)
    },
    [authDetails, brooklynUrl, onSuccess],
  )

  const tuple = useMemo(
    () => [remove, { isConfigLoading, status, error }] as const,
    [remove, isConfigLoading, status, error],
  )
  return tuple
}
